/**
 * Starea conversației cu widgetul, per tab (sessionStorage) - supraviețuiește
 * navigării/refresh-ului în același tab, dispare la închiderea lui. Ține două
 * lucruri:
 *
 *  - firul de discuție, ca vizitatorul să nu-l piardă la un refresh (istoricul
 *    trimis modelului e cu totul altul: îl citește serverul din `conversations`
 *    după sessionId - vezi services/conversations.ts);
 *  - intenția de continuare: când agentul cere o navigare, întrebarea trebuie să
 *    supraviețuiască încărcării noii pagini, ca să putem răspunde acolo;
 *  - trail-ul de acțiuni: ultimele ACTION_TRAIL_MAX_EVENTS acțiuni ale
 *    vizitatorului (nav/click/submit), trimise cu fiecare /ask ca modelul să
 *    vadă drumul până la întrebare (actionTrail.ts).
 *
 * Continuarea și trail-ul stau în ACEEAȘI cheie, nu în chei noi: cookies.html
 * publică un tabel al stocării locale despre care afirmă că e exhaustiv, deci o
 * cheie nouă n-ar fi o decizie tehnică, ci o modificare a unui document public
 * (registru → G12). Cheia e mai ieftin de refolosit decât promisiunea de
 * renegociat. ATENȚIE: descrierea cheii din cookies.html spunea „is not sent
 * back to the AI model" - trail-ul PLEACĂ la model, deci propoziția aceea se
 * actualizează în același commit cu feature-ul (vezi registrul).
 */
import { ACTION_TRAIL_MAX_EVENTS, type UserActionEvent } from "@ai-guidance/shared";

export interface StoredChatMessage {
  role: "user" | "agent";
  text: string;
}

/** Întrebarea care așteaptă noua pagină ca să primească răspuns. */
export interface PendingContinuation {
  question: string;
  /** câte navigări a făcut deja lanțul ăsta - plasa de siguranță e a serverului, asta doar oprește drumul degeaba */
  hops: number;
  /**
   * Momentul click-ului (Date.now). Opțional doar la citire: o intenție fără el
   * a fost scrisă de un SDK vechi din cache-ul browserului - o tratăm ca
   * expirată (vezi `takePendingContinuation`). Scrierile noi îl pun mereu.
   */
  at?: number;
}

/**
 * Sosirea normală a continuării e la secunde după click (cât ține un reload).
 * Peste atât, click-ul pe linkul evidențiat n-a dus nicăieri (a fost anulat,
 * blocat, sau linkul nici nu naviga) și intenția a rămas atârnând - ar trage
 * un ask fantomă la o încărcare de pagină viitoare, complet nelegată. TTL-ul o
 * lasă să expire în loc.
 */
const CONTINUATION_TTL_MS = 60_000;

export interface ChatState {
  messages: StoredChatMessage[];
  pendingContinuation?: PendingContinuation;
  actions?: UserActionEvent[];
}

const HISTORY_LIMIT = 40;

function storageKey(appId: string): string {
  return `aig_chat_${appId}`;
}

/**
 * Formatul cheii a fost cândva un array simplu de mesaje, iar sessionStorage-ul
 * vizitatorilor aflați acum în tab-uri deschise încă îl conține: citim și forma
 * veche, scriem doar forma nouă.
 */
function loadState(appId: string): ChatState {
  try {
    const raw = sessionStorage.getItem(storageKey(appId));
    if (!raw) return { messages: [] };
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return { messages: parsed as StoredChatMessage[] };
    const state = parsed as Partial<ChatState>;
    return {
      messages: Array.isArray(state.messages) ? state.messages : [],
      ...(state.pendingContinuation ? { pendingContinuation: state.pendingContinuation } : {}),
      ...(Array.isArray(state.actions) ? { actions: state.actions } : {}),
    };
  } catch {
    return { messages: [] };
  }
}

function saveState(appId: string, state: ChatState): void {
  try {
    sessionStorage.setItem(storageKey(appId), JSON.stringify(state));
  } catch {
    // storage indisponibil (private mode etc.) - conversația rămâne doar în memorie
  }
}

export function loadChatHistory(appId: string): StoredChatMessage[] {
  return loadState(appId).messages;
}

/**
 * Starea completă a conversației (mesaje + intenție de continuare + trail), ca
 * s-o cărăm întreagă la un salt cross-subdomeniu (chatHandoff.ts). sessionStorage
 * e per-origine - fără asta, pe alt subdomeniu declarat conversația repornește.
 */
export function exportChatState(appId: string): ChatState {
  return loadState(appId);
}

/** Restaurează starea cărată pe originea nouă. Trecută prin aceleași plafoane ca la scriere. */
export function restoreChatState(appId: string, state: ChatState): void {
  saveState(appId, {
    messages: Array.isArray(state.messages) ? state.messages.slice(-HISTORY_LIMIT) : [],
    ...(state.pendingContinuation ? { pendingContinuation: state.pendingContinuation } : {}),
    ...(Array.isArray(state.actions) ? { actions: state.actions.slice(-ACTION_TRAIL_MAX_EVENTS) } : {}),
  });
}

/**
 * Șterge toată cheia (mesaje + intenție de continuare + trail) - nu doar
 * mesajele. Folosită la închiderea explicită a widgetului cât timp contul e
 * epuizat (sdk.ts): fără istoric, următoarea încărcare din acest tab nu mai
 * are conversație de continuat, deci widgetul nu se mai montează deloc (vezi
 * ramura din `init`). O intenție de continuare rămasă ar fi oricum orfană -
 * niciun widget n-o mai poate consuma.
 */
export function clearChatHistory(appId: string): void {
  try {
    sessionStorage.removeItem(storageKey(appId));
  } catch {
    // storage indisponibil (private mode etc.) - nimic de curățat
  }
}

export function appendChatMessage(appId: string, message: StoredChatMessage): void {
  const state = loadState(appId);
  saveState(appId, { ...state, messages: [...state.messages, message].slice(-HISTORY_LIMIT) });
}

export function setPendingContinuation(appId: string, pending: PendingContinuation): void {
  saveState(appId, { ...loadState(appId), pendingContinuation: pending });
}

/**
 * Citește ȘI șterge intenția, într-o singură operație: dacă cererea de
 * continuare eșuează (rețea, cotă), nu vrem s-o reîncercăm la fiecare încărcare
 * de pagină din tab-ul ăsta până la închiderea lui. O intenție expirată (sau
 * fără `at`, deci scrisă de un SDK vechi) se șterge la fel, dar întoarce `null`
 * - pagina nu declanșează niciun ask pentru ea.
 */
export function takePendingContinuation(appId: string): PendingContinuation | null {
  const state = loadState(appId);
  if (!state.pendingContinuation) return null;
  const { pendingContinuation, ...rest } = state;
  saveState(appId, rest); // consumat necondiționat: și una expirată trebuie ștearsă
  if (pendingContinuation.at === undefined || Date.now() - pendingContinuation.at > CONTINUATION_TTL_MS) {
    return null;
  }
  return pendingContinuation;
}

export function loadActionTrail(appId: string): UserActionEvent[] {
  return loadState(appId).actions ?? [];
}

/**
 * Adaugă o acțiune în trail, cu două reguli:
 *  - acțiunile identice consecutive (același kind + path + label) se colapsează
 *    în una singură cu contor `n` - trei click-uri pe „Pay now" sunt UN rând
 *    „(3 times)", nu trei rânduri;
 *  - ring buffer: peste plafon, cea mai veche cade.
 */
export function recordAction(appId: string, event: UserActionEvent): void {
  const state = loadState(appId);
  const actions = [...(state.actions ?? [])];
  const last = actions[actions.length - 1];
  if (last && last.kind === event.kind && last.path === event.path && last.label === event.label) {
    actions[actions.length - 1] = { ...last, t: event.t, n: (last.n ?? 1) + 1 };
  } else {
    actions.push(event);
  }
  saveState(appId, { ...state, actions: actions.slice(-ACTION_TRAIL_MAX_EVENTS) });
}
